/**
 * Kono internal: the customer side of an RFQ in one place. The email thread
 * (everything that went to the customer and came back, from the platform and
 * from the parsed BDR mailboxes), and "where we are": the last step taken
 * and the next one proposed, with the button that prepares the email.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { BetterPrice, EmailThread, EmailThreadEntry, fetchRfqEmailThread, suggestCustomerFollowUpNow } from "./api";

const when = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

const daysSince = (iso: string): number => Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);

const KIND_LABEL: Record<string, string> = {
  "customer-request": "Customer's request",
  "customer-message": "Customer's message",
  "customer-reply": "Customer replied",
  "hand-reply": "Answered by hand from the mailbox",
  "hand-follow-up": "Follow-up by hand from the mailbox",
  "hand-quote-email": "Quote emailed by hand from the mailbox",
  "quote-ready": "Quote email",
  quote: "Quote PDF",
  "quote-share": "Quote shared",
  "quote-link": "Quote link sent",
  acknowledgment: "Acknowledgment",
  "follow-up": "Follow-up",
  "partial-available": "Partial availability",
  "sourcing-acknowledgment": "Sourcing acknowledgment",
};

const kindLabel = (e: EmailThreadEntry): string => KIND_LABEL[e.kind] || e.kind.replace(/-/g, " ");

const describe = (e: EmailThreadEntry): string => {
  const who = e.direction === "in" ? e.from || "the customer" : e.by && e.by !== "system" ? e.by : "the platform";
  const base = `${kindLabel(e)}${e.quoteReference ? ` (${e.quoteReference})` : ""} ${e.direction === "in" ? "from" : "by"} ${who}`;
  return `${base} ${when(e.at)}`;
};

/** Every email around the RFQ, oldest first; our own emails open to their text. */
export const EmailThreadPanel = ({ rfqId, refreshKey = 0, onLoaded }: { rfqId: string; refreshKey?: number; onLoaded?: (thread: EmailThread) => void }) => {
  const [thread, setThread] = useState<EmailThread | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const t = await fetchRfqEmailThread(rfqId);
      setThread(t);
      onLoaded?.(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the emails.");
    }
  }, [rfqId, onLoaded]);
  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const entries = (thread?.entries || []).filter((e) => !e.held);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm">
      <h4 className="font-semibold text-[#00062e]">
        Email thread with the customer
        {entries.length > 0 && <span className="ml-2 text-xs font-normal text-gray-500">{entries.length} email{entries.length === 1 ? "" : "s"}</span>}
      </h4>
      <p className="mt-0.5 text-xs text-gray-500">
        Everything recorded around this RFQ and its quote{(thread?.quoteReferences.length || 0) > 1 ? "s" : ""}
        {thread?.quoteReferences.length ? ` (${thread.quoteReferences.join(", ")})` : ""}: emails the platform sent, with opens and clicks, and what the parsed BDR mailboxes saw go out or come back. An email sent from a mailbox that is not parsed cannot appear here.
      </p>
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
      {thread && entries.length === 0 && <p className="mt-2 text-xs text-gray-500">No email recorded yet.</p>}
      <ol className="mt-2 space-y-1.5">
        {entries.map((e, i) => (
          <li key={`${e.at}-${i}`} className={`rounded-lg border px-3 py-2 ${e.direction === "in" ? "border-sky-200 bg-sky-50/60" : "border-gray-200 bg-gray-50/60"}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
              <span className={`rounded px-1.5 py-0.5 font-medium ${e.direction === "in" ? "bg-sky-100 text-sky-800" : "bg-gray-200 text-gray-800"}`}>{e.direction === "in" ? "← in" : "→ out"}</span>
              <span className="font-medium text-gray-900">{kindLabel(e)}</span>
              {e.quoteReference && <span className="text-gray-500">{e.quoteReference}</span>}
              <span className="text-gray-500">{when(e.at)}</span>
              {e.source === "mailbox" && <span className="rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800" title="Seen in a parsed BDR mailbox, not sent by the platform">mailbox</span>}
              {e.tracking && (
                <span className="text-gray-500">
                  {e.tracking.bounced ? "bounced" : e.tracking.clicks ? `clicked ${e.tracking.clicks}×` : e.tracking.opens ? `opened ${e.tracking.opens}×` : e.tracking.delivered ? "delivered, not opened" : "sent"}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-xs text-gray-700">
              <span className="font-medium">{e.subject || "(no subject)"}</span>
              <span className="text-gray-500">
                {" "}
                · {e.direction === "in" ? `from ${e.from || "?"}` : `to ${e.to.join(", ") || "?"}${e.by && e.by !== "system" ? ` · by ${e.by}` : ""}`}
              </span>
            </div>
            {e.text && (
              <details className="mt-1">
                <summary className="cursor-pointer text-[11px] text-gray-500">Read</summary>
                <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap font-sans text-xs text-gray-800">{e.text}</pre>
              </details>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export type NextStepInput = {
  rfqId: string;
  status: string;
  quoteReference: string | null;
  customerEmailState: string | null;
  thread: EmailThread | null;
  betterPrice: BetterPrice | null;
  vendorsWaiting: number;
  vendorsResponded: number;
  /** Days without a customer answer after which the follow-up is proposed. */
  followUpAfterDays?: number;
  onSuggested: () => void;
  onError: (message: string) => void;
};

/** The last step taken with the customer and the next one proposed, with the button that prepares the email. */
export const NextStepCard = (p: NextStepInput) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const entries = useMemo(() => (p.thread?.entries || []).filter((e) => !e.held), [p.thread]);
  const last = entries[entries.length - 1] || null;
  const lastOut = [...entries].reverse().find((e) => e.direction === "out") || null;
  const lastIn = [...entries].reverse().find((e) => e.direction === "in") || null;
  const customerAnsweredAfterQuote = !!(lastIn && lastOut && Date.parse(lastIn.at) > Date.parse(lastOut.at));
  const followUpAfter = p.followUpAfterDays ?? 2;

  type Step = { title: string; why: string; action?: { label: string; trigger: "no-open" | "after-click" } ; hint?: string; tone: "amber" | "rose" | "emerald" | "gray" };
  const next: Step = (() => {
    const bp = p.betterPrice;
    if (bp && bp.status === "open") {
      const cheaper = bp.lines.filter((l) => l.kind === "cheaper").length;
      const found = bp.lines.length - cheaper;
      return {
        title: `Send the customer a new quote: ${[cheaper ? `${cheaper} better price${cheaper === 1 ? "" : "s"}` : "", found ? `${found} item${found === 1 ? "" : "s"} found` : ""].filter(Boolean).join(" and ")} from ${bp.vendors.map((v) => v.supplierName).filter((n, i, a) => a.indexOf(n) === i).join(", ")}`,
        why: "A vendor answered after the quote with a better price or an item the quote could not cover. In the Vendors column, 'Lower price option' writes the customer email (\"we kept working on this for you...\"), you edit and approve, and the next quote version is prepared.",
        hint: "Use the Lower price option panel in the Vendors column.",
        tone: "rose",
      };
    }
    if (p.status === "waiting-vendors") {
      return {
        title: p.vendorsWaiting ? `Waiting on ${p.vendorsWaiting} vendor${p.vendorsWaiting === 1 ? "" : "s"}; ${p.vendorsResponded} answered` : "Generate the quote",
        why: p.vendorsWaiting ? "Chase the vendors still quiet from the Vendors column, or generate the quote with what is in." : "Every vendor asked has answered: generate the quote and prepare the customer email.",
        tone: "amber",
      };
    }
    if (p.customerEmailState === "prepared" || p.customerEmailState === "draft") {
      return {
        title: `Send quote ${p.quoteReference || ""} to the customer`,
        why: "The quote exists but the platform has no record of this version going out. Send it from the Customer email panel, or, if you already emailed it from your own mailbox, leave it (a parsed mailbox is checked hourly and the status updates by itself).",
        tone: "amber",
      };
    }
    if (p.status === "converted") {
      return { title: "Ordered", why: "The customer placed the order. Nothing to send; the order flow takes it from here.", tone: "emerald" };
    }
    if (p.status === "closed") {
      return { title: "Closed", why: "No quote went out. Reopen the RFQ to put it back in front of the vendors.", tone: "gray" };
    }
    if (p.status === "quoted" && lastOut) {
      if (customerAnsweredAfterQuote) {
        return {
          title: `Answer the customer: they wrote back ${when(lastIn!.at)}`,
          why: `"${lastIn!.subject}" came in after our last email. Read it in the thread and reply from your mailbox, or send a revised quote.`,
          tone: "amber",
        };
      }
      const days = daysSince(lastOut.at);
      const opened = !!lastOut.tracking && (lastOut.tracking.opens > 0 || lastOut.tracking.clicks > 0);
      if (days >= followUpAfter) {
        return {
          title: `Follow up with the customer on quote ${p.quoteReference || ""}`,
          why: `Our last email ("${lastOut.subject}") went out ${days} day${days === 1 ? "" : "s"} ago${lastOut.tracking ? (opened ? " and was opened" : " and was not opened") : ""}; no answer yet. The platform writes the follow-up in the team's voice; you review it before anything is sent.`,
          action: { label: "Suggest the follow-up email", trigger: opened ? "after-click" : "no-open" },
          tone: "amber",
        };
      }
      return {
        title: `Give the customer time: quote sent ${days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} ago`}`,
        why: `A follow-up is proposed after ${followUpAfter} days without an answer${lastOut.tracking ? ` (opens and clicks are tracked)` : ""}.`,
        action: { label: "Suggest a follow-up now anyway", trigger: opened ? "after-click" : "no-open" },
        tone: "gray",
      };
    }
    return { title: "Quote the customer", why: "Pick the items in Sourcing and generate the quote.", tone: "gray" };
  })();

  const suggest = async (trigger: "no-open" | "after-click") => {
    setBusy(true);
    setNote("");
    try {
      await suggestCustomerFollowUpNow(p.rfqId, trigger);
      setNote("The follow-up is written and waiting under Customer emails below: read it, edit it, send it.");
      p.onSuggested();
    } catch (e) {
      p.onError(e instanceof Error ? e.message : "Could not write the follow-up.");
    } finally {
      setBusy(false);
    }
  };

  const tone = { amber: "border-amber-300 bg-amber-50", rose: "border-red-300 bg-red-50", emerald: "border-emerald-200 bg-emerald-50", gray: "border-gray-200 bg-gray-50" }[next.tone];
  return (
    <div className={`rounded-xl border p-4 text-sm ${tone}`}>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Last step</div>
          <div className="mt-0.5 text-gray-900">{last ? describe(last) : "Nothing emailed yet"}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Proposed next step</div>
          <div className={`mt-0.5 font-semibold ${next.tone === "rose" ? "text-red-800" : "text-gray-900"}`}>{next.title}</div>
          <p className="mt-0.5 text-xs text-gray-700">{next.why}</p>
          {next.action && (
            <button type="button" disabled={busy} className="mt-2 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50" onClick={() => void suggest(next.action!.trigger)}>
              {busy ? "Writing…" : next.action.label}
            </button>
          )}
          {note && <p className="mt-1 text-xs text-teal-800">{note}</p>}
        </div>
      </div>
    </div>
  );
};
